import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer';
import { sendWarning } from '@components/Noti';
import Slider from '@components/Slider';
import Textarea from '@components/Textarea';
import { Validator } from '@components/Validator';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import {
  $GateMapMarkerTip,
  $editingMapMarkerApi,
} from '@stores/editingMarkerStores';
import {
  ImageType,
  type STMapMarker,
  type STMapMarkerPolyline,
  STMapMarkerType,
} from '@types';
import cn from '@utils/cn';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import flexBoxHack from '@utils/flexBoxHack';
import { hexToColorString } from '@utils/hexToColorString';
import { useGate } from 'effector-react';
import type { Draft } from 'immer';
import { toPercent } from 'pano-utils';
import { useEffect, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import useConstant from 'use-constant';
import { stEditorAmap } from '../STAMap';
import { LINE_ANIMATION_ICONS } from '../constants';

interface Props {
  marker: STMapMarkerPolyline;
  isNew: boolean;
}

const strokeTypeItems: DropdownItem<
  STMapMarkerPolyline['icon']['strokeStyle']['type']
>[] = [
  { id: 'solid', name: '实线' },
  { id: 'dashed', name: '虚线' },
];

const animationTypeItems: DropdownItem<
  STMapMarkerPolyline['icon']['animation']['type']
>[] = [
  { id: 0, name: '无动效' },
  { id: 1, name: '轨迹运动' },
];

const valToString = (val: number) => toPercent(val, 0);

const defaultLineColor = hexToColorString({ hex: '#286EFA', alpha: 1 });
const defaultBorderColor = hexToColorString({ hex: '#1A1A1A', alpha: 1 });

export function FeatureSettingsPolyline({ marker, isNew }: Props) {
  const { path, borderStyle, strokeStyle, animation } = marker.icon;
  const [isEditing, setIsEditing] = useState(isNew);
  const mouseTool = useConstant(
    () => new AMap.MouseTool(stEditorAmap.getMap())
  );
  const editor = useConstant(
    () => new AMap.PolylineEditor(stEditorAmap.getMap())
  );
  const [title, setTitle] = useState(marker.title);

  useGate(
    $GateMapMarkerTip,
    (() => {
      if (isEditing) {
        return path.length
          ? [
              '按住空心圆点拖拽调整节点位置',
              '按住实心圆点拖拽新增节点',
              '单击空心圆点删除节点',
            ]
          : ['点击任意位置开始绘制多边形', '双击结束绘制'];
      }
      return [];
    })()
  );

  const toggleDraw = (bool: boolean) => {
    if (bool) {
      mouseTool.polyline({
        strokeColor: strokeStyle.color,
        strokeOpacity: strokeStyle.opacity,
        strokeWeight: strokeStyle.width,
        strokeStyle: strokeStyle.type,
        borderWeight: borderStyle.width,
        outlineColor: borderStyle.color,
        isOutline: !!borderStyle.width,
      });
    } else {
      mouseTool.close(true);
    }
  };

  useEffect(() => {
    const onDraw = (event: any) => {
      const obj = event.obj;
      const pathArr = obj.getPath();
      if (pathArr.length < 2) {
        obj.setMap(null);
        sendWarning('请至少绘制两个点');
      } else {
        updateMarker((draft) => {
          draft.icon.path = pathArr.map((item: any) => [item.lng, item.lat]);
        });
        mouseTool.close(true);
        setIsEditing(false);
      }
    };

    const handleEvent = (event: any) => {
      updateMarker((draft) => {
        draft.icon.path = event.target
          .getPath()
          .map((item: any) => [item.lng, item.lat]);
      });
    };

    editor.on('addnode', handleEvent);
    editor.on('adjust', handleEvent);
    editor.on('removenode', handleEvent);
    editor.on('end', handleEvent);

    mouseTool.on('draw', onDraw);
    return () => {
      mouseTool.off('draw', onDraw);
      mouseTool.close(true);

      editor.off('addnode', handleEvent);
      editor.off('adjust', handleEvent);
      editor.off('removenode', handleEvent);
      editor.off('end', handleEvent);
      editor.close();
    };
  }, []);

  useEffect(() => {
    if (isEditing) {
      if (!path.length) {
        toggleDraw(true);
      } else {
        const polyline = stEditorAmap.getMarkerObjectById(marker.id);
        if (polyline) {
          editor.setTarget(polyline as AMap.Polyline);
          editor.open();
        }
      }

      const animationMarker = stEditorAmap.getAnimationMarkerById(marker.id);
      if (animationMarker) {
        animationMarker.stopMove();
        animationMarker.setMap(null);
      }
    } else {
      editor.close();
    }
  }, [isEditing]);

  const lineColor = useMemo(() => {
    return hexToColorString({
      hex: strokeStyle.color,
      alpha: strokeStyle.opacity,
    });
  }, [strokeStyle]);

  const borderColor = useMemo(() => {
    return hexToColorString({ hex: borderStyle.color });
  }, [borderStyle.color]);

  useEffect(() => {
    if (isEditing) return;
    stEditorAmap.addMarker(marker);
  }, [marker, isEditing]);

  const updateMarker = (
    updater: (draft: Draft<STMapMarkerPolyline>) => STMapMarker | void
  ) => {
    $editingMapMarkerApi.update((draft) => {
      draft.type ??= STMapMarkerType.polyline;
      if (draft.type !== STMapMarkerType.polyline) return;
      updater(draft);
    });
  };

  const handlers = useTextareaHandelrs('trimRight', (val) => {
    updateMarker((draft) => {
      draft.title = val;
    });
  });

  const renderEmpty = () => {
    return (
      <div className="px-10 py-15 bg-[#282828] rounded">
        请在地图中单击任意位置开始绘制折线,双击结束绘制.
      </div>
    );
  };

  const renderEditing = () => {
    return (
      <div className="px-10 py-15 bg-[#282828] rounded">
        请在地图中进行折线标记的编辑操作
        <Button
          className="w-full mt-20"
          onClick={() => {
            setIsEditing(false);
          }}
        >
          完成编辑
        </Button>
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <>
        <Collapse title="折线样式">
          <ColorPicker
            className="mt-20"
            colorString={lineColor}
            onChange={(colorString) => {
              const { hex, alpha } = colorStringToHexAndAlpha(colorString);
              updateMarker((draft) => {
                draft.icon.strokeStyle.color = hex;
                draft.icon.strokeStyle.opacity = alpha;
              });
            }}
          >
            <ColorPicker.Header>
              线条颜色
              <ColorPicker.Reset defaultColor={defaultLineColor} />
            </ColorPicker.Header>
            <ColorPicker.Bar />
          </ColorPicker>
          <Slider
            className="mt-20"
            min={1}
            max={20}
            value={strokeStyle.width}
            valToString={(val) => `${val}px`}
            onChange={(val) => {
              updateMarker((draft) => {
                draft.icon.strokeStyle.width = val;
              });
            }}
          >
            <Slider.Header title="线条粗细" />
            <Slider.Bar />
            <Slider.Footer />
          </Slider>
          <div className="mt-20">线条样式</div>
          <Dropdown
            className="mt-10"
            items={strokeTypeItems}
            selected={strokeStyle.type}
            onChange={(val) => {
              updateMarker((draft) => {
                draft.icon.strokeStyle.type = val;
              });
            }}
          />
          <ColorPicker
            className="mt-20"
            colorString={borderColor}
            onChange={(colorString) => {
              const { hex } = colorStringToHexAndAlpha(colorString);
              updateMarker((draft) => {
                draft.icon.borderStyle.color = hex;
              });
            }}
          >
            <ColorPicker.Header>
              描边颜色
              <ColorPicker.Reset defaultColor={defaultBorderColor} />
            </ColorPicker.Header>
            <ColorPicker.Bar disableAlpha />
          </ColorPicker>
          <Slider
            className="mt-20"
            min={0}
            max={10}
            value={borderStyle.width}
            valToString={(val) => `${val}px`}
            onChange={(val) => {
              updateMarker((draft) => {
                draft.icon.borderStyle.width = val;
              });
            }}
          >
            <Slider.Header title="描边粗细" />
            <Slider.Bar />
            <Slider.Footer />
          </Slider>
          <Button
            className="w-full mt-20"
            onClick={() => {
              setIsEditing(true);
            }}
          >
            编辑折线
          </Button>
        </Collapse>
        <Collapse title="折线动效">
          <Dropdown
            items={animationTypeItems}
            selected={animation.type}
            onChange={(val) => {
              updateMarker((draft) => {
                draft.icon.animation.type = val;
                const iconItem = LINE_ANIMATION_ICONS[0];
                draft.icon.animation.markerIcon = iconItem.url;
                draft.icon.animation.markerWidth = iconItem.width;
                draft.icon.animation.markerHeight = iconItem.height;
              });
            }}
          />
          {animation.type === 1 && (
            <>
              <ImageContainer className="h-140 mt-20">
                <Scrollbars>
                  <div className="flex flex-wrap items-center justify-between p-5">
                    {LINE_ANIMATION_ICONS.map((x, i) => {
                      return (
                        <Image
                          key={i}
                          className={cn('mb-10 h-45 w-45 cursor-pointer', {
                            'ring-2 ring-inset ring-orange':
                              x.url === animation.markerIcon,
                          })}
                          objectFit="contain"
                          src={x.url}
                          type={ImageType.sysMedia}
                          onClick={() => {
                            updateMarker((draft) => {
                              draft.icon.animation.markerIcon = x.url;
                              draft.icon.animation.markerWidth = x.width;
                              draft.icon.animation.markerHeight = x.height;
                            });
                          }}
                        />
                      );
                    })}
                    {flexBoxHack(45, 4)}
                  </div>
                </Scrollbars>
              </ImageContainer>
              <Slider
                className="mt-20"
                min={0.1}
                max={2}
                value={animation.markerScale ?? 1}
                valToString={valToString}
                onChange={(val) => {
                  updateMarker((draft) => {
                    draft.icon.animation.markerScale = val;
                  });
                }}
              >
                <Slider.Header title="图标缩放：" />
                <Slider.Bar step={0.01} />
                <Slider.Control step={0.01} />
              </Slider>
            </>
          )}
        </Collapse>
        <Collapse title="标记标题">
          <Textarea
            value={title}
            scheme="dark"
            className="mb-20 h-115"
            placeholder="请输入标记标题"
            maxLength={500}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handlers.onBlur}
          />
        </Collapse>
      </>
    );
  };

  return (
    <Validator
      value={path.length}
      validFn={(val) => !!val}
      warningMessage="请先绘制折线"
    >
      {({ value, warning }) => {
        if (!value) return renderEmpty();
        return isEditing ? renderEditing() : renderSettings();
      }}
    </Validator>
  );
}
